import type {
  LeagueRulesProps,
  SeasonStructure,
  ScoringRule,
  ScoringExample,
  TeamEntry,
  TeamSelectionRules,
  TiebreakerRules,
  PrizeStructure,
} from '../types'

// =============================================================================
// Section Card wrapper
// =============================================================================

function RuleCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-xl border border-zinc-200 dark:border-zinc-800
                        bg-white dark:bg-zinc-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-zinc-100 dark:border-zinc-800
                      sm:px-5">
        <h2 className="text-sm font-bold uppercase tracking-wider text-zinc-900 dark:text-zinc-100">
          {title}
        </h2>
      </div>
      <div className="px-4 py-4
                      sm:px-5 sm:py-5">
        {children}
      </div>
    </section>
  )
}

// =============================================================================
// 1. Season Structure
// =============================================================================

function SeasonStructureCard({ season }: { season: SeasonStructure }) {
  return (
    <RuleCard title="Season Structure">
      <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
        {season.description}
      </p>

      <div className="grid grid-cols-2 gap-3">
        {season.halves.map((half) => (
          <div
            key={half.name}
            className="rounded-lg bg-zinc-50 dark:bg-zinc-800/50 p-3 text-center"
          >
            <span className="text-xs font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
              {half.name}
            </span>
            <div className="text-lg font-bold tabular-nums text-zinc-900 dark:text-zinc-100 mt-1">
              MD {half.matchdays}
            </div>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-md
                               bg-violet-100 dark:bg-violet-900/40
                               text-violet-700 dark:text-violet-300
                               text-[10px] font-bold uppercase tracking-wide">
                {half.tddAllowance} DD
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400">
        <span className="inline-flex items-center justify-center w-4 h-4 rounded-full
                         bg-zinc-200 dark:bg-zinc-700 text-[9px] font-bold">!</span>
        DD does not carry over between halves
      </div>
    </RuleCard>
  )
}

// =============================================================================
// 2. Scoring Rules
// =============================================================================

function PointsBadge({ points, color }: { points: number | null; color: string }) {
  if (points === null) {
    return (
      <span className="inline-flex items-center justify-center min-w-[2rem] px-1.5 py-0.5 rounded-md
                        text-xs font-bold tabular-nums
                        bg-violet-100 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400">
        2&times;
      </span>
    )
  }

  const isPositive = points > 0
  const isNegative = points < 0

  return (
    <span className={`inline-flex items-center justify-center min-w-[2rem] px-1.5 py-0.5 rounded-md
                      text-xs font-bold tabular-nums
                      ${isPositive ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300' : ''}
                      ${isNegative ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' : ''}
                      ${!isPositive && !isNegative ? 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400' : ''}`}>
      {isPositive && '+'}{points}
    </span>
  )
}

function ScoringRulesCard({ rules, examples }: { rules: ScoringRule[]; examples: ScoringExample[] }) {
  return (
    <RuleCard title="Scoring Rules">
      {/* Rules table */}
      <div className="space-y-2.5">
        {rules.map((rule) => (
          <div key={rule.id} className="flex items-start gap-3">
            <PointsBadge points={rule.points} color={rule.color} />
            <div className="flex-1 min-w-0">
              <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {rule.label}
              </span>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                {rule.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Scoring examples */}
      <div className="mt-6 pt-5 border-t border-zinc-100 dark:border-zinc-800">
        <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-400 dark:text-zinc-500 mb-3">
          Examples
        </h3>
        <div className="space-y-3">
          {examples.map((ex) => (
            <ScoringExampleRow key={ex.id} example={ex} />
          ))}
        </div>
      </div>
    </RuleCard>
  )
}

function ScoringExampleRow({ example }: { example: ScoringExample }) {
  const isPositiveTotal = example.total > 0
  const isNegativeTotal = example.total < 0

  return (
    <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/50 p-3">
      {/* Header: title + match */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">
            {example.title}
          </span>
          {example.tddUsed && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-md border
                             bg-violet-100 dark:bg-violet-900/40
                             text-violet-700 dark:text-violet-300
                             border-violet-200 dark:border-violet-800
                             text-[10px] font-semibold uppercase tracking-wide shrink-0">
              DD
            </span>
          )}
        </div>
        <span className={`text-lg font-bold tabular-nums shrink-0
                          ${isPositiveTotal ? 'text-emerald-600 dark:text-emerald-400' : ''}
                          ${isNegativeTotal ? 'text-red-600 dark:text-red-400' : ''}
                          ${!isPositiveTotal && !isNegativeTotal ? 'text-zinc-400 dark:text-zinc-500' : ''}`}>
          {isPositiveTotal && '+'}{example.total} pts
        </span>
      </div>

      {/* Match context */}
      <div className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">
        {example.team} {example.score} {example.opponent}
        <span className="ml-1.5 text-zinc-400 dark:text-zinc-500">
          ({example.isHome ? 'H' : 'A'})
        </span>
      </div>

      {/* Breakdown */}
      <div className="space-y-1">
        {example.breakdown.map((item, i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-zinc-600 dark:text-zinc-400">{item.label}</span>
            <span className={`font-bold tabular-nums
                              ${item.points > 0 ? 'text-emerald-600 dark:text-emerald-400' : ''}
                              ${item.points < 0 ? 'text-red-600 dark:text-red-400' : ''}
                              ${item.points === 0 ? 'text-zinc-400 dark:text-zinc-500' : ''}`}>
              {item.points > 0 && '+'}{item.points}
            </span>
          </div>
        ))}
      </div>

      {/* Note */}
      {example.note && (
        <p className="text-[11px] text-zinc-400 dark:text-zinc-500 mt-2 leading-relaxed italic">
          {example.note}
        </p>
      )}
    </div>
  )
}

// =============================================================================
// Helpers
// =============================================================================

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return n + (s[(v - 20) % 10] || s[v] || s[0])
}

// =============================================================================
// 3 & 4. DD Team Cards
// =============================================================================

function TeamBadge({ team }: { team: TeamEntry }) {
  const isUrl = team.logo.startsWith('http://') || team.logo.startsWith('https://') || team.logo.startsWith('/')

  return (
    <div className="flex items-center gap-2 rounded-lg bg-zinc-50 dark:bg-zinc-800/50
                    px-2.5 py-2">
      <div className="w-6 h-6 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden shrink-0
                      flex items-center justify-center">
        {isUrl ? (
          <img
            src={team.logo}
            alt={team.shortName}
            className="w-5 h-5 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
            }}
          />
        ) : (
          <span className="text-[10px] font-bold text-zinc-500 dark:text-zinc-400">
            {team.shortName.slice(0, 2).toUpperCase()}
          </span>
        )}
      </div>
      <div className="min-w-0">
        <span className="text-xs font-semibold text-zinc-900 dark:text-zinc-100 leading-tight block truncate">
          {team.shortName}
        </span>
        {team.promoted ? (
          <span className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
            Promoted
          </span>
        ) : team.priorSeasonPosition ? (
          <span className="text-[10px] text-zinc-400 dark:text-zinc-500">
            {ordinal(team.priorSeasonPosition)}
          </span>
        ) : null}
      </div>
    </div>
  )
}

function TddTeamsCard({ title, subtitle, teams }: {
  title: string
  subtitle: string
  teams: TeamEntry[]
}) {
  return (
    <RuleCard title={title}>
      <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-3">
        {subtitle}
      </p>
      <div className="grid grid-cols-2 gap-2
                      sm:grid-cols-3
                      md:grid-cols-4">
        {teams.map((team) => (
          <TeamBadge key={team.id} team={team} />
        ))}
      </div>
    </RuleCard>
  )
}

// =============================================================================
// 5. Team Selection Rules
// =============================================================================

function TeamSelectionCard({ rules }: { rules: TeamSelectionRules }) {
  return (
    <RuleCard title="Team Selection">
      <div className="space-y-3">
        {rules.rules.map((rule, i) => (
          <div key={i} className="flex items-start gap-3">
            <span className="flex items-center justify-center w-5 h-5 rounded-full shrink-0
                             bg-emerald-100 dark:bg-emerald-900/40
                             text-emerald-700 dark:text-emerald-300
                             text-[10px] font-bold mt-0.5">
              {i + 1}
            </span>
            <p className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed">
              {rule}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 p-3">
        <div className="flex items-start gap-2">
          <span className="text-amber-500 text-sm mt-0.5 shrink-0">&#9888;</span>
          <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            <span className="font-semibold">Autopick:</span> {rules.autopickMethod}
          </p>
        </div>
      </div>
    </RuleCard>
  )
}

// =============================================================================
// 6. Tiebreaker Rules
// =============================================================================

function TiebreakersCard({ rules }: { rules: TiebreakerRules }) {
  return (
    <RuleCard title="Tiebreakers">
      <div className="space-y-0">
        {rules.order.map((step, i) => {
          const isLast = i === rules.order.length - 1
          return (
            <div key={step.priority} className="flex items-start gap-3 relative">
              {/* Vertical connector line */}
              {!isLast && (
                <div className="absolute left-2.5 top-6 w-px h-[calc(100%-4px)]
                                bg-zinc-200 dark:bg-zinc-700" />
              )}
              <span className="flex items-center justify-center w-5 h-5 rounded-full shrink-0 z-10
                               bg-zinc-900 dark:bg-zinc-100
                               text-white dark:text-zinc-900
                               text-[10px] font-bold mt-0.5">
                {step.priority}
              </span>
              <div className="flex-1 pb-4">
                <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                  {step.label}
                </span>
                <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          )
        })}
      </div>
    </RuleCard>
  )
}

// =============================================================================
// 7. Prize Structure
// =============================================================================

function PrizeCard({ prizes }: { prizes: PrizeStructure }) {
  const placeEmojis = ['', '1st', '2nd', '3rd']

  return (
    <RuleCard title="Prize Structure">
      <div className="space-y-2">
        {prizes.prizes.map((prize) => (
          <div
            key={prize.place}
            className="flex items-center justify-between rounded-lg
                       bg-zinc-50 dark:bg-zinc-800/50 px-3 py-2.5"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-zinc-400 dark:text-zinc-500 w-6">
                {placeEmojis[prize.place] || ''}
              </span>
              <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">
                {prize.label}
              </span>
            </div>
            <span className="text-sm font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
              ${prize.amount}
            </span>
          </div>
        ))}
      </div>

      {/* Last place penalty */}
      <div className="mt-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-red-800 dark:text-red-300">
              Last Place
            </span>
          </div>
          <span className="text-sm font-bold tabular-nums text-red-600 dark:text-red-400">
            -${prizes.lastPlacePenalty.amount}
          </span>
        </div>
        <p className="text-xs text-red-700 dark:text-red-400 mt-1 leading-relaxed">
          {prizes.lastPlacePenalty.description}
        </p>
      </div>
    </RuleCard>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function LeagueRules({
  seasonStructure,
  scoringRules,
  scoringExamples,
  tddEligibleTeams,
  tddEligibleSubtitle,
  tddIneligibleTeams,
  tddIneligibleSubtitle,
  teamSelectionRules,
  tiebreakerRules,
  prizeStructure,
}: LeagueRulesProps) {
  return (
    <div className="max-w-2xl mx-auto py-6
                    sm:px-6
                    md:px-0">
      {/* Page header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
          League Rules
        </h1>
        <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5">
          {seasonStructure.season} Season
        </p>
      </div>

      {/* Rule cards */}
      <div className="space-y-4">
        <SeasonStructureCard season={seasonStructure} />

        <ScoringRulesCard rules={scoringRules} examples={scoringExamples} />

        <TddTeamsCard
          title="DD Eligible Teams"
          subtitle={tddEligibleSubtitle}
          teams={tddEligibleTeams}
        />

        <TddTeamsCard
          title="DD Ineligible Teams"
          subtitle={tddIneligibleSubtitle}
          teams={tddIneligibleTeams}
        />

        <TeamSelectionCard rules={teamSelectionRules} />

        <TiebreakersCard rules={tiebreakerRules} />

        {prizeStructure && <PrizeCard prizes={prizeStructure} />}
      </div>
    </div>
  )
}
